import type { FunctionComponent } from "react";
import { useCallback } from "react";
import { graphql, useFragment } from "react-relay";

import { sendGATracking } from "~/components/tracking/google-analytics/apis";
import { useTrackImpressionEventByIntersection } from "~/components/tracking/google-analytics/hooks";
import type {
  CommentButtonImpressionEvents,
  CommentButtonLocation,
} from "~/components/tracking/google-analytics/types";
import { useArticleCommentLink } from "~/lib/hooks";
import type { articleCommentsTriggerArticle$key } from "~/queries/__generated__/articleCommentsTriggerArticle.graphql";

import { ArticleCommentsBanner } from "./banner";
import { ArticleCommentsButton } from "./button";
import { ArticleCommentsWidget } from "./widget";

export type Props = {
  className?: string;
  location: CommentButtonLocation;
  reference: articleCommentsTriggerArticle$key;
  variant: "widget" | "button" | "banner";
};

export const ArticleCommentsTrigger: FunctionComponent<Props> = ({
  className,
  location,
  reference: reference_,
  variant,
}) => {
  const { commentCount } = useFragment<articleCommentsTriggerArticle$key>(
    graphql`
      fragment articleCommentsTriggerArticle on Article {
        commentCount
      }
    `,
    reference_,
  );

  const impressionEvent = useCallback<() => CommentButtonImpressionEvents>(
    () => ({
      action: "Comment button/Impression",
      category: "Comment",
      label: `Comment button/Impression/${location}`,
    }),
    [location],
  );
  const { captureTrackImpressionEventTargetElement } = useTrackImpressionEventByIntersection(
    impressionEvent,
    { shouldSendOnce: true },
  );
  const { toggleByUrl: toggleCommentByUrl } = useArticleCommentLink();

  const handleOpenComments = () => {
    sendGATracking({
      action: "Comment button/Click",
      category: "Comment",
      label: `Comment button/Click/${location}`,
    });
    toggleCommentByUrl();
  };

  switch (variant) {
    case "button":
      return (
        <ArticleCommentsButton
          className={className}
          commentCount={commentCount ?? 0}
          ref={captureTrackImpressionEventTargetElement}
          onClick={handleOpenComments}
        />
      );
    case "banner":
      return (
        <ArticleCommentsBanner
          className={className}
          commentCount={commentCount ?? 0}
          ref={captureTrackImpressionEventTargetElement}
          onClick={handleOpenComments}
        />
      );
    case "widget":
      return (
        <ArticleCommentsWidget
          className={className}
          commentCount={commentCount ?? 0}
          ref={captureTrackImpressionEventTargetElement}
          onClick={handleOpenComments}
        />
      );
    default:
      return null;
  }
};
